import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import type { I18n, Person, ProjectCategory, Task, TaskPriority, TaskStatus } from '../types'

/**
 * Le panneau d'édition d'une tâche.
 *
 * Une tâche se crée en une frappe — le titre suffit — puis s'affine ici :
 * catégorie, statut, priorité, responsable, collaborateurs, échéance,
 * avancement. Séparer les deux gestes est délibéré : demander la colonne et le
 * responsable avant d'avoir écrit le titre est la friction qui fait qu'on note
 * la tâche ailleurs, sur un papier qu'on perd.
 *
 * **Responsable et collaborateurs ne sont pas la même chose.** Le premier
 * répond de la tâche, les seconds y travaillent. Les confondre fait qu'à la
 * fin personne n'en répond — d'où deux champs distincts plutôt qu'une liste
 * de « personnes concernées ».
 *
 * La suppression est dans le panneau, à sa place : en bas, séparée, avec une
 * confirmation. Une croix sur la carte du kanban se cliquerait par accident au
 * milieu d'un glissement.
 */
export interface TaskDraft {
  title: string
  description: string | null
  category_id: string | null
  status_id: string
  priority_id: string | null
  owner_user_id: string | null
  assignee_ids: string[]
  due_at: string | null
  progress: number
  notes: string | null
}

/** `datetime-local` veut `AAAA-MM-JJTHH:MM` ; l'API parle ISO avec fuseau. */
function toLocalInput(value: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  const pad = (part: number) => String(part).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null
}

export function TaskEditor({
  task,
  statuses,
  priorities,
  categories,
  people,
  label,
  busy,
  error,
  onSave,
  onDelete,
  onClose,
}: {
  task: Task
  statuses: TaskStatus[]
  priorities: TaskPriority[]
  categories: ProjectCategory[]
  people: Person[]
  label: (value: I18n) => string
  busy: boolean
  error: string | null
  onSave: (draft: Partial<TaskDraft>) => void
  onDelete: () => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<TaskDraft>({
    title: task.title,
    description: task.description,
    category_id: task.category_id,
    status_id: task.status_id,
    priority_id: task.priority_id,
    owner_user_id: task.owner_user_id,
    assignee_ids: task.assignee_ids,
    due_at: task.due_at,
    progress: task.progress,
    notes: task.notes,
  })
  const [confirming, setConfirming] = useState(false)

  const set = <K extends keyof TaskDraft>(key: K, value: TaskDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  const toggleAssignee = (id: string) =>
    setDraft((current) => ({
      ...current,
      assignee_ids: current.assignee_ids.includes(id)
        ? current.assignee_ids.filter((row) => row !== id)
        : [...current.assignee_ids, id],
    }))

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/50" role="dialog" aria-modal>
      {/* Le fond ferme le panneau : c'est le geste qu'on tente d'instinct. */}
      <button
        type="button"
        aria-label={t('admin.project.close')}
        className="flex-1 cursor-default"
        onClick={onClose}
      />

      <div className="flex w-full max-w-lg flex-col overflow-y-auto border-l border-line bg-bg-base p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-mono text-caption text-content-muted">{task.reference}</p>
            {/* L'auteur, constaté et non modifiable.
                Il n'y a délibérément pas de champ : le serveur l'inscrit
                depuis la session, et proposer de le choisir laisserait croire
                qu'on peut signer à la place de quelqu'un d'autre. */}
            {task.created_by_label ? (
              <p className="mt-0.5 text-caption text-content-muted">
                {t('admin.project.createdBy')} : {task.created_by_label}
              </p>
            ) : null}
            {task.is_overdue ? (
              <Badge tone="ember">{t('admin.project.overdue')}</Badge>
            ) : null}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('admin.project.close')}
          </Button>
        </div>

        <div className="mt-5 space-y-4">
          <Field label={t('admin.project.taskTitle')}>
            <input
              value={draft.title}
              onChange={(event) => set('title', event.target.value)}
              className={INPUT}
            />
          </Field>

          <Field label={t('admin.project.descriptionField')}>
            <textarea
              rows={3}
              value={draft.description ?? ''}
              onChange={(event) => set('description', event.target.value || null)}
              className={cn(INPUT, 'h-auto py-2')}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('admin.project.category')}>
              <select
                value={draft.category_id ?? ''}
                onChange={(event) => set('category_id', event.target.value || null)}
                className={INPUT}
              >
                <option value="">{t('admin.project.noCategory')}</option>
                {categories.map((row) => (
                  <option key={row.id} value={row.id}>
                    {label(row.name_i18n)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t('admin.project.status')}>
              <select
                value={draft.status_id}
                onChange={(event) => set('status_id', event.target.value)}
                className={INPUT}
              >
                {statuses.map((row) => (
                  <option key={row.id} value={row.id}>
                    {label(row.name_i18n)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t('admin.project.priority')}>
              <select
                value={draft.priority_id ?? ''}
                onChange={(event) => set('priority_id', event.target.value || null)}
                className={INPUT}
              >
                <option value="">{t('admin.project.noPriority')}</option>
                {priorities.map((row) => (
                  <option key={row.id} value={row.id}>
                    {label(row.name_i18n)}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label={t('admin.project.owner')}
              hint={t('admin.project.ownerHint')}
            >
              <select
                value={draft.owner_user_id ?? ''}
                onChange={(event) => set('owner_user_id', event.target.value || null)}
                className={INPUT}
              >
                <option value="">{t('admin.project.noOwner')}</option>
                {people.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t('admin.project.due')}>
              <input
                type="datetime-local"
                value={toLocalInput(draft.due_at)}
                onChange={(event) => set('due_at', fromLocalInput(event.target.value))}
                className={INPUT}
              />
            </Field>

            <Field label={t('admin.project.progress')}>
              <input
                type="number"
                min={0}
                max={100}
                value={draft.progress}
                onChange={(event) =>
                  set('progress', Math.max(0, Math.min(100, Number(event.target.value))))
                }
                className={cn(INPUT, 'tabular-nums')}
              />
            </Field>
          </div>

          {/* Les collaborateurs : plusieurs, cochés. Un `<select multiple>` se
              manipule mal à la souris et pas du tout au doigt. */}
          <Field
            label={t('admin.project.assignees')}
            hint={t('admin.project.assigneesHint')}
          >
            <div className="mt-1 flex flex-wrap gap-2">
              {people.map((row) => {
                const on = draft.assignee_ids.includes(row.id)
                return (
                  <button
                    key={row.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleAssignee(row.id)}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-caption transition',
                      on
                        ? 'border-champagne-400 text-champagne-300'
                        : 'border-line text-content-muted hover:text-content-secondary',
                    )}
                  >
                    {row.label}
                  </button>
                )
              })}
              {people.length === 0 ? (
                <p className="text-caption text-content-muted">
                  {t('admin.project.noPeople')}
                </p>
              ) : null}
            </div>
          </Field>

          <Field label={t('admin.project.notes')}>
            <textarea
              rows={2}
              value={draft.notes ?? ''}
              onChange={(event) => set('notes', event.target.value || null)}
              className={cn(INPUT, 'h-auto py-2')}
            />
          </Field>
        </div>

        {error ? <p className="mt-4 text-sm text-ember-300">{error}</p> : null}

        <div className="mt-6 flex flex-wrap gap-3">
          <Button disabled={busy || !draft.title.trim()} onClick={() => onSave(draft)}>
            {t('admin.project.save')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('admin.project.cancel')}
          </Button>
        </div>

        {/* La suppression en bas, séparée, et confirmée. */}
        <div className="mt-8 border-t border-line-subtle pt-5">
          {confirming ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-content-secondary">
                {t('admin.project.deleteConfirm')}
              </p>
              <Button size="sm" disabled={busy} onClick={onDelete}>
                {t('admin.project.deleteYes')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                {t('admin.project.cancel')}
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
              {t('admin.project.delete')}
            </Button>
          )}
        </div>

        <p className="mt-6 text-caption text-content-muted">
          {t('admin.project.createdAt', {
            at: new Date(task.created_at).toLocaleString(),
          })}
          {task.updated_at
            ? ` · ${t('admin.project.updatedAt', {
                at: new Date(task.updated_at).toLocaleString(),
              })}`
            : ''}
        </p>
      </div>
    </div>
  )
}

const INPUT =
  'mt-1 h-11 w-full rounded-md border border-line bg-bg-elevated px-3 text-sm text-content-primary'

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block text-caption text-content-muted">
      {label}
      {children}
      {hint ? <span className="mt-1 block text-caption text-content-muted">{hint}</span> : null}
    </label>
  )
}
